
import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';

export default function ParklandsPage() {
  useEffect(() => {
    document.title = 'Parklands Painters Cape Town | Family Residential Painting | Suburban Community Services';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Professional painting services in Parklands, Cape Town. Expert family home painting, residential estate services, and community-focused painting contractors. Quality workmanship for Parklands families.');
    }

    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'Parklands painters, family home painting, residential estate Cape Town');
    }

    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute('href', `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/areas/parklands`);
    }

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "name": "Parklands Painters Cape Town",
      "description": "Professional painting services in Parklands, Cape Town",
      "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/areas/parklands`,
      "areaServed": {
        "@type": "Place",
        "name": "Parklands, Cape Town, Western Cape, South Africa"
      },
      "serviceArea": {
        "@type": "GeoCircle",
        "geoMidpoint": {
          "@type": "GeoCoordinates",
          "latitude": -33.8567,
          "longitude": 18.5234
        },
        "geoRadius": "15000"
      }
    });
    document.head.appendChild(script);

    return () => {
      const existingScript = document.querySelector('script[type="application/ld+json"]');
      if (existingScript && existingScript.textContent?.includes('Parklands Painters')) {
        existingScript.remove();
      }
    };
  }, []);

  return (
    <div className="min-h-screen">
      <Header />
      
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-gray-900 to-black text-white py-20">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/8cfb53a1da128d9475c2e419399830c3.jpg"
            alt="Professional painting services in Parklands, Cape Town"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-black/70"></div>
        </div>
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl lg:text-6xl font-bold leading-tight mb-6">
              Trusted <span className="text-yellow-400">Parklands Painters</span>
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed mb-8">
              Professional painting services for Parklands families and community. Quality residential painting with family-friendly service and competitive pricing for your suburban home.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="bg-yellow-400 text-black px-8 py-4 rounded-lg text-lg font-semibold hover:bg-yellow-500 transition-colors text-center whitespace-nowrap cursor-pointer"
              >
                Get Quote
              </Link>
              <Link
                to="/services"
                className="bg-white text-black px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-colors text-center whitespace-nowrap cursor-pointer border border-gray-200"
              >
                Services
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-black mb-6">
              Family-Focused Painting Services in Parklands
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive painting solutions designed for Parklands families, from interior makeovers to exterior protection, with child-safe materials and flexible scheduling.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: 'ri-home-heart-line',
                title: 'Family Home Painting',
                description: 'Complete home painting services using child-safe, low-VOC paints with family-friendly scheduling and minimal disruption.',
                features: ['Child-safe materials', 'Low-VOC paints', 'Family scheduling', 'Quick turnaround']
              },
              {
                icon: 'ri-community-line',
                title: 'Residential Estates',
                description: 'Professional painting for residential estates, complexes, and gated communities in the Parklands area.',
                features: ['Estate complexes', 'Gated communities', 'Common areas', 'Coordinated projects']
              },
              {
                icon: 'ri-palette-line',
                title: 'Interior Design Support',
                description: 'Color consultation and interior design support to help families create beautiful, functional living spaces.',
                features: ['Color consultation', 'Design advice', 'Trend guidance', 'Family-friendly colors']
              }
            ].map((service, index) => (
              <div key={index} className="bg-gray-50 rounded-2xl p-8 hover:shadow-lg transition-shadow">
                <div className="w-16 h-16 flex items-center justify-center mx-auto mb-6" style={{backgroundColor: '#ffff00'}}>
                  <i className={`${service.icon} text-black text-2xl`}></i>
                </div>
                <h3 className="text-2xl font-bold text-black mb-4">{service.title}</h3>
                <p className="text-gray-600 mb-6 leading-relaxed">{service.description}</p>
                <ul className="space-y-2">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center text-gray-700">
                      <div className="w-16 h-16 flex items-center justify-center mx-auto mb-6" style={{backgroundColor: '#ffff00'}}>
                        <i className={`${feature.icon} text-black text-2xl`}></i>
                      </div>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl font-bold text-black mb-8">
                Why Parklands Families Choose Our Painting Services
              </h2>
              <div className="space-y-6">
                {[
                  {
                    title: 'Family-Friendly Approach',
                    description: 'Understanding of family needs with child-safe materials, flexible scheduling, and minimal disruption to daily routines.'
                  },
                  {
                    title: 'Community Reputation',
                    description: 'Trusted by the Parklands community with numerous satisfied families and positive local references.'
                  },
                  {
                    title: 'Quality & Safety',
                    description: 'Premium, non-toxic paints and materials ensuring a safe environment for children and pets.'
                  },
                  {
                    title: 'Competitive Family Pricing',
                    description: 'Fair, transparent pricing with family-friendly payment options and excellent value for money.'
                  }
                ].map((benefit, index) => (
                  <div key={index} className="flex items-start space-x-4">
                    <div className="w-8 h-8 bg-yellow-400 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <i className="ri-check-line text-black text-sm"></i>
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-black mb-2">{benefit.title}</h3>
                      <p className="text-gray-600 leading-relaxed">{benefit.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <img
                src="https://readdy.ai/api/search-image?query=Professional%20painters%20working%20on%20Parklands%20family%20home%2C%20suburban%20residential%20painting%2C%20family-friendly%20painting%20service%2C%20safe%20painting%20materials%20and%20equipment%2C%20Cape%20Town%20residential%20area&width=600&height=700&seq=parklands-team-work&orientation=portrait"
                alt="Family-friendly painting services in Parklands"
                className="rounded-2xl shadow-xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Recent Projects */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-black mb-6">
              Recent Parklands Family Projects
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Explore our latest family home painting projects in Parklands, showcasing our commitment to quality and family-focused service.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                title: 'Modern Family Home Makeover',
                location: 'Parklands North',
                description: 'Complete interior and exterior transformation of a 4-bedroom family home with child-safe materials.',
                image: 'https://readdy.ai/api/search-image?query=Modern%20family%20home%20makeover%20in%20Parklands%20North%20Cape%20Town%2C%20contemporary%20residential%20architecture%2C%20professional%20family%20home%20painting%2C%20suburban%20house%20with%20garden&width=400&height=300&seq=parklands-project-1&orientation=landscape'
              },
              {
                title: 'Residential Estate Complex',
                location: 'Parklands Central',
                description: 'Coordinated painting project for a 30-unit residential estate including common areas and facilities.',
                image: 'https://readdy.ai/api/search-image?query=Residential%20estate%20complex%20painting%20in%20Parklands%20Central%2C%20multi-unit%20residential%20development%2C%20coordinated%20painting%20project%2C%20modern%20suburban%20estate%20Cape%20Town&width=400&height=300&seq=parklands-project-2&orientation=landscape'
              },
              {
                title: 'Young Family Nursery',
                location: 'Parklands East',
                description: 'Specialized nursery and children\'s room painting using non-toxic, child-safe materials.',
                image: 'https://readdy.ai/api/search-image?query=Childrens%20nursery%20room%20painting%20in%20Parklands%20East%2C%20family%20home%20interior%20with%20child-safe%20materials%2C%20colorful%20kids%20room%20painting%2C%20safe%20family%20environment%20Cape%20Town&width=400&height=300&seq=parklands-project-3&orientation=landscape'
              }
            ].map((project, index) => (
              <div key={index} className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                <img
                  src={project.image}
                  alt={project.title}
                  className="w-full h-48 object-cover"
                />
                <div className="p-6">
                  <div className="flex items-center text-sm text-gray-500 mb-2">
                    <i className="ri-map-pin-line mr-2"></i>
                    {project.location}
                  </div>
                  <h3 className="text-xl font-bold text-black mb-3">{project.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{project.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-black mb-6">
              What Parklands Families Say
            </h2>
            <p className="text-xl text-gray-600">
              Read testimonials from satisfied families in the Parklands community
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                name: 'Michelle Roberts',
                location: 'Parklands North',
                rating: 5,
                text: 'Fantastic service for our family home. They worked around our children\'s schedules and used safe materials. Our house looks amazing and the kids love their new rooms!'
              },
              {
                name: 'David Chen',
                location: 'Parklands Central',
                rating: 5,
                text: 'Professional coordination for our estate complex painting. Excellent communication with all residents and minimal disruption. Great results throughout the development.'
              },
              {
                name: 'Lisa van Wyk',
                location: 'Parklands East',
                rating: 5,
                text: 'The team was wonderful with our young children around. They took extra care with safety and the nursery looks beautiful. Highly recommend for families with kids.'
              }
            ].map((testimonial, index) => (
              <div key={index} className="bg-white rounded-2xl p-8 shadow-lg">
                <div className="flex items-center mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-yellow-400 text-lg"></i>
                  ))}
                </div>
                <p className="text-gray-600 leading-relaxed mb-6 italic">"{testimonial.text}"</p>
                <div>
                  <div className="font-semibold text-black">{testimonial.name}</div>
                  <div className="text-sm text-gray-500">{testimonial.location}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-yellow-400">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-black mb-6">
            Transform Your Parklands Family Home
          </h2>
          <p className="text-xl text-black/80 mb-8 max-w-3xl mx-auto">
            Contact us for a family-friendly consultation and detailed quote. Experience safe, professional painting services designed for Parklands families.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="bg-black text-white px-10 py-4 rounded-lg text-xl font-bold hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer"
            >
              Get Quote
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
